import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import AlertModal from '../../AlertModal';
import AddEditTimeOffLogModal from './AddEditTimeOffLogModal';
import PaginationComponent from './Pagination';
import moment from 'moment';

const PAGE_SIZE = 15;

const TimeOffLogTab = ({ teamMembers = [], isTeamMembersLoading = false, onRefreshTeamMembers }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [timeOffLogs, setTimeOffLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editTimeOffLog, setEditTimeOffLog] = useState(null);
  const [timeOffLogToDelete, setTimeOffLogToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 1
  });

  const fetchTimeOffLogs = async (page, { showLoading = false } = {}) => {
    if (showLoading) setIsFetchingData(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.TIME_OFF_LOGS_LIST,
      method: 'POST',
      requestBody: {
        page,
        pageSize: PAGE_SIZE,
        sort: {
          createdAt: -1
        },
        query: searchQuery.trim() || undefined
      }
    });

    if (error) {
      toast.error(error);
      if (showLoading) setIsFetchingData(false);
      return;
    }

    const mapped = (response?.results || []).map(item => ({
      _id: item._id,
      userId: item.userId,
      name: item?.userId?.name || 'Unknown User',
      startDate: item.startDate,
      endDate: item.endDate || item.startDate,
      workDays: typeof item.workDays === 'number' && !Number.isNaN(item.workDays) ? item.workDays : 0,
      notes: item.notes
    }));

    setTimeOffLogs(mapped);

    const total = response?.total ?? mapped.length;
    const nextTotalPages =
      typeof response?.totalPages === 'number' && response.totalPages >= 1
        ? response.totalPages
        : Math.max(1, Math.ceil(total / PAGE_SIZE));

    setPaginationMeta(prev => ({
      ...prev,
      totalCount: total,
      totalPages: nextTotalPages,
      currentPage: mapped.length === 0 && page > 1 ? Math.max(1, page - 1) : page
    }));

    if (showLoading) setIsFetchingData(false);
  };

  useLayoutEffect(() => {
    setPaginationMeta(prev => ({ ...prev, currentPage: 1 }));
  }, [searchQuery]);

  useEffect(() => {
    fetchTimeOffLogs(paginationMeta.currentPage, { showLoading: true });
  }, [searchQuery, paginationMeta.currentPage]);

  const handleAddEditTimeOffLog = async values => {
    setLoading(true);

    const requestBody = {
      userId: values.userId,
      startDate: values.startDate,
      endDate: values.endDate || undefined,
      notes: values.notes
    };

    const { error } = await makeRESTApiRequest({
      endpoint: editTimeOffLog?._id ? ENDPOINTS.TIME_OFF_LOGS_WITH_ID(editTimeOffLog._id) : ENDPOINTS.TIME_OFF_LOGS,
      method: editTimeOffLog?._id ? 'PUT' : 'POST',
      requestBody
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    toast.success(editTimeOffLog?._id ? 'Time off log updated successfully' : 'Time off log added successfully');
    setShowAddEditModal(false);
    setEditTimeOffLog(null);
    await Promise.all([
      fetchTimeOffLogs(paginationMeta.currentPage),
      onRefreshTeamMembers ? onRefreshTeamMembers() : Promise.resolve()
    ]);
    setLoading(false);
  };

  const handleDeleteTimeOffLog = async id => {
    setLoading(true);

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.TIME_OFF_LOGS_WITH_ID(id),
      method: 'DELETE'
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    toast.success('Time off log deleted successfully');
    await Promise.all([
      fetchTimeOffLogs(paginationMeta.currentPage),
      onRefreshTeamMembers ? onRefreshTeamMembers() : Promise.resolve()
    ]);
    setLoading(false);
    setShowDeleteAlert(false);
    setTimeOffLogToDelete(null);
  };

  const isLogInActive = log => {
    const today = moment().startOf('day');
    const end = moment(log.endDate).startOf('day');
    return today.isAfter(end, 'day');
  };

  return (
    <div>
      <div
        className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center mb-3"
        style={{ gap: 12 }}
      >
        <div className="position-relative flex-grow-1">
          <Search
            className="icon-md position-absolute"
            style={{
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: '#6c757d'
            }}
          />
          <Form.Control
            type="text"
            size="lg"
            className="bg-white search-input text-dark xsmallFont"
            placeholder="Search by name, notes, or date..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 42, border: '1px solid #e5e7ea', fontWeight: 'normal' }}
          />
        </div>
        <Button
          variant="success"
          className="d-flex align-items-center flex-shrink-0"
          onClick={() => {
            setEditTimeOffLog(null);
            setShowAddEditModal(true);
          }}
          style={{ minHeight: '40px' }}
        >
          <Plus size={16} className="mr-1" />
          Add Time Off
        </Button>
      </div>

      {isFetchingData ? (
          <TimeOffLogCardSkeleton />
      ) : timeOffLogs.length === 0 ? (
        <div className="mt-4 p-4 text-center largeFont text-muted">No time off entries</div>
      ) : (
        <>
          <div className="row mt-3">
            {timeOffLogs.map(log => {
              const isInActive = isLogInActive(log);
              return (
                <div key={log._id} className="col-12 col-md-6 col-lg-4 mb-3">
                <div
                  className="position-relative px-4 py-3 border h-100 bg-white"
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    opacity: isInActive ? 0.58 : 1,
                    minHeight: 100
                  }}
                >
                  <div className="d-flex" style={{ position: 'absolute', top: 10, right: 12, gap: 4 }}>
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 border-0 shadow-none bg-transparent hover-muted"
                      title="Edit"
                      disabled={isFetchingData || loading}
                      onClick={() => {
                        setEditTimeOffLog(log);
                        setShowAddEditModal(true);
                      }}
                    >
                      <Pencil size={16} className="text-muted" />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 border-0 shadow-none bg-transparent hover-muted"
                      title="Delete"
                      disabled={isFetchingData || loading}
                      onClick={() => {
                        setTimeOffLogToDelete(log._id);
                        setShowDeleteAlert(true);
                      }}
                    >
                      <Trash2 size={16} className="text-danger" />
                    </Button>
                  </div>
                  <div className="pr-5">
                    <div className="d-flex align-items-center">
                      <span className="font-weight-bold mr-2" style={{ color: '#111827' }}>
                        {log.name}
                      </span>
                      {!isInActive ? (
                        <span
                          aria-label="Active or upcoming"
                          title="Active or upcoming"
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#22c55e',
                            display: 'inline-block'
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="mt-1 small text-muted">
                      {moment(log.startDate).format('MMMM D, YYYY')} - {moment(log.endDate).format('MMMM D, YYYY')} (
                      {log.workDays} day{log.workDays === 1 ? '' : 's'})
                    </div>
                    {log.notes ? (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id={`time-off-notes-${log._id}`}>
                            {log.notes}
                          </Tooltip>
                        }
                      >
                        <div className="mt-1 small text-muted text-clamp-1" style={{ cursor: 'default' }}>
                          {log.notes}
                        </div>
                      </OverlayTrigger>
                    ) : null}
                  </div>
                </div>
              </div>
                );
            })}
          </div>
          <PaginationComponent
            paginationMeta={paginationMeta}
            pageSize={PAGE_SIZE}
            disabled={isFetchingData || loading}
            onPageChange={page => setPaginationMeta(prev => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
      <AddEditTimeOffLogModal
        show={showAddEditModal}
        onHide={() => {
          setShowAddEditModal(false);
          setEditTimeOffLog(null);
        }}
        onSubmit={handleAddEditTimeOffLog}
        showProgress={loading}
        edit={editTimeOffLog}
        teamMembers={teamMembers}
        isTeamMembersLoading={isTeamMembersLoading}
      />
      <AlertModal
        show={showDeleteAlert}
        onHide={() => setShowDeleteAlert(false)}
        onContinueClick={() => timeOffLogToDelete && handleDeleteTimeOffLog(timeOffLogToDelete)}
        onDismissClick={() => {
          setShowDeleteAlert(false);
          setTimeOffLogToDelete(null);
        }}
        showProgress={loading}
        alertText="Are you sure you want to delete this time off log?"
        progressText="Deleting time off log..."
      />
    </div>
  );
};

export default TimeOffLogTab;

const TimeOffLogCardSkeleton = () => {
  return (
    <div className="row mt-3">
    {Array.from({ length: 3 }).map((_, index) => (
      <div className="col-12 col-md-6 col-lg-4 mb-3" key={index}>
      <div
        className="position-relative px-4 py-3 border h-100"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        aria-hidden
      >
        <div className="d-flex" style={{ position: 'absolute', top: 10, right: 12, gap: 4 }}>
          <div className="shimmer rounded" style={{ width: 16, height: 16 }} />
          <div className="shimmer rounded" style={{ width: 16, height: 16 }} />
        </div>
        <div className="pr-5">
          <span className="font-weight-bold" style={{ color: '#111827' }}>
            <span
              className="shimmer rounded d-inline-block"
              style={{ width: '12rem', maxWidth: '100%', height: '1.125rem', verticalAlign: 'middle' }}
            />
          </span>
          <div className="mt-1 small text-muted">
            <span className="shimmer rounded d-inline-block" style={{ width: '100%', height: '1em', verticalAlign: 'middle' }} />
          </div>
          <div className="mt-1 small text-muted text-clamp-1">
            <span className="shimmer rounded d-inline-block" style={{ width: '58%', height: '1em', verticalAlign: 'middle' }} />
          </div>
        </div>
      </div>
    </div>
    ))}
    </div>
  );
};
